import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../contexts/app.context';
import Input from '../input';
import {
  arrivalTimes,
  classLevels,
  departureTimes,
  isDepartureAfterArrival,
  modesOfTransport,
  NUMBER_OF_TOUR_STUDENTS,
  picnicTimeSlots,
  visitDays,
} from './data';
import Text from '../text';
import { scrollToClass } from './util';
import { getBookedTimeSlots } from '../../service';
import { OpenIcon } from '../../assets/icons';
import { BiTrash } from 'react-icons/bi';
import Popup from '../popup';

/**
 * @param {
 *  type: school | other-school | other-group,
 *  objectName: string,
 *  formik: formik instance,
 *  translationPrefix: string,
 *  openClassIndex: number,
 *  experienceInteractionConfig: {
 *    visible: boolean,
 *    editable: boolean,
 *  }
 * } props
 * @returns
 */
const ClassDetailsForm = ({
  type = 'school',
  objectName = 'classes',
  formik,
  translationPrefix,
  openClassIndex,
  experienceInteractionConfig,
}) => {
  const { t } = useTranslation();
  const { visible, editable, newBooking, finalBookingTimePassed, slotEditTimePassed } =
    experienceInteractionConfig;

  const { profile } = useContext(AppContext);

  const [openClass, setOpenClass] = useState(0);
  const [openDeletePopup, setOpenDeletePopup] = useState(null);
  const [bookedTimeSlots, setBookedTimeSlots] = useState({});

  const classDetailsFormikValues = formik.values?.[objectName] || [];

  const classDetailRef = useRef(null);
  const savedClassSlotsRef = useRef({});
  const isLocalhost = window.location.hostname === 'localhost';

  useEffect(() => {
    (formik.values?.[objectName] || []).forEach((classDetail) => {
      if (!classDetail._id || savedClassSlotsRef.current[classDetail._id]) return;
      savedClassSlotsRef.current[classDetail._id] = {
        visitDay: classDetail.visitDay,
        arrivalTime: classDetail.arrivalTime,
      };
    });
  }, [formik.values, objectName]);

  useEffect(() => {
    if (openClassIndex) {
      setOpenClass(openClassIndex);
    }
  }, [openClassIndex]);

  useEffect(() => {
    if (finalBookingTimePassed) return;

    const fetchBookedTimeSlots = async () => {
      try {
        const { data } = await getBookedTimeSlots();
        setBookedTimeSlots(data || {});
      } catch (error) {
        console.log(error);
      }
    };

    fetchBookedTimeSlots();
  }, [finalBookingTimePassed]);

  const getArrivalTimeOptions = (visitDay, currentArrivalTime, classIndex) => {
    return (arrivalTimes[visitDay] || []).map((time) => {
      if (finalBookingTimePassed) return time;

      const slotData = bookedTimeSlots?.[visitDay]?.introduction?.[time.value];
      const isCurrent = currentArrivalTime === time.value;
      const localTaken = classDetailsFormikValues.filter((classDetail, index) => {
        if (index === classIndex) return false;
        if (classDetail.visitDay !== visitDay || classDetail.arrivalTime !== time.value) {
          return false;
        }
        if (!classDetail._id) return true;

        const savedSlot = savedClassSlotsRef.current[classDetail._id];
        return !(savedSlot?.visitDay === visitDay && savedSlot?.arrivalTime === time.value);
      }).length;
      const remaining =
        time.slots == null
          ? null
          : (slotData?.available !== undefined ? slotData.available : time.slots) - localTaken;
      const available = remaining == null ? true : isCurrent || remaining > 0;
      const label =
        isLocalhost && remaining != null ? `${time.label} (${remaining} restants)` : time.label;

      return { ...time, label, disabled: !available };
    });
  };

  const getDepartureTimeOptions = (visitDay, arrivalTime) => {
    const options = departureTimes[visitDay] || [];
    if (!arrivalTime) return options;
    return options.filter((time) => isDepartureAfterArrival(arrivalTime, time.value));
  };

  const setIntroductionBooking = (index, arrivalTime, extra = {}) => {
    formik.setFieldValue(`${objectName}.${index}.bookExperience`, 'true');
    formik.setFieldValue(`${objectName}.${index}.experienceDetails.tourType`, 'introduction');
    formik.setFieldValue(`${objectName}.${index}.experienceDetails.timeSlot`, arrivalTime || '');
    Object.entries(extra).forEach(([key, value]) => {
      formik.setFieldValue(`${objectName}.${index}.${key}`, value);
    });
  };

  return (
    <div className="w-full flex flex-col gap-6">
      {classDetailsFormikValues.map((classDetail, index) => {
        const slotLocked = slotEditTimePassed && Boolean(classDetail._id);
        return (
          <div
            key={index}
            ref={classDetailRef}
            id={`class-${index}`}
            className="w-full overflow-hidden rounded-lg border-2 border-teal-20">
            <div
              className="w-full px-2 py-3 bg-teal-20 relative text-center cursor-pointer user-select-none"
              onClick={() => {
                if (openClass === index) {
                  setOpenClass(null);
                } else {
                  setOpenClass(index);
                  scrollToClass(`class-${index}`);
                }
              }}>
              <span className="absolute top-0 left-0 h-full flex items-center justify-center px-4">
                <OpenIcon
                  className={`transition-transform duration-300 text-navy-10 ${
                    openClass === index ? '' : 'rotate-180'
                  }`}
                />
              </span>
              {!profile?.loggedIn && (
                <span
                  className="absolute top-0 right-0 h-full flex items-center justify-center px-4"
                  onClick={(e) => {
                    e.stopPropagation();

                    setOpenDeletePopup(index);
                  }}>
                  <BiTrash className="text-navy-10" />
                </span>
              )}
              <span
                className="text-navy-10 text-xs"
                dangerouslySetInnerHTML={{
                  __html:
                    type === 'school'
                      ? t(`${translationPrefix}.classDetailsForm.title`) +
                        ' ' +
                        `<span class='font-bold'>${index + 1}</span>`
                      : type === 'other-school'
                        ? t(`${translationPrefix}.classDetailsForm.title`) +
                          ' ' +
                          `<span class='font-bold'>${index + 1}</span>`
                        : t(`${translationPrefix}.classDetailsForm.titleGroup`) +
                          ' ' +
                          `<span class='font-bold'>${index + 1}</span>`,
                }}
              />
            </div>
            {openClass === index && (
              <div className="w-full px-6 py-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1">
                    <Input
                      id={`${objectName}.${index}.name`}
                      name={`${objectName}.${index}.name`}
                      label={t(`${translationPrefix}.classDetailsForm.name.label`)}
                      placeholder={t(`${translationPrefix}.classDetailsForm.name.placeholder`)}
                      defaultValue={classDetail.name}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['name'] &&
                        formik.errors[`${objectName}`]?.[index]?.['name']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(`${objectName}.${index}.name`, e.target.value);
                      }}
                    />
                  </div>
                  {type !== 'other-group' && (
                    <div className="col-span-1">
                      {type === 'school' && (
                        <div className="flex w-full flex-col gap-4">
                          <Input
                            type={'select'}
                            options={classLevels}
                            id={`${objectName}.${index}.level`}
                            name={`${objectName}.${index}.level`}
                            label={t(`${translationPrefix}.classDetailsForm.level.label`)}
                            placeholder={t(
                              `${translationPrefix}.classDetailsForm.level.placeholder`,
                            )}
                            defaultValue={classDetail.level}
                            error={
                              formik.touched?.[`${objectName}`]?.[index]?.['level'] &&
                              formik.errors[`${objectName}`]?.[index]?.['level']
                            }
                            onChange={(e) => {
                              formik.setFieldValue(`${objectName}.${index}.level`, e.target.value);
                              formik.setFieldValue(
                                `${objectName}.${index}.experienceDetails.level`,
                                e.target.value,
                              );
                              setIntroductionBooking(index, classDetail.arrivalTime);
                            }}
                          />

                          {classDetail.level === 'other' && (
                            <Input
                              type={'text'}
                              id={`${objectName}.${index}.otherLevel`}
                              name={`${objectName}.${index}.otherLevel`}
                              label={t(`${translationPrefix}.classDetailsForm.otherLevel.label`)}
                              placeholder={t(
                                `${translationPrefix}.classDetailsForm.otherLevel.placeholder`,
                              )}
                              defaultValue={classDetail.otherLevel}
                              error={
                                formik.touched?.[`${objectName}`]?.[index]?.['otherLevel'] &&
                                formik.errors[`${objectName}`]?.[index]?.['otherLevel']
                              }
                              onChange={(e) => {
                                formik.setFieldValue(
                                  `${objectName}.${index}.otherLevel`,
                                  e.target.value,
                                );
                              }}
                            />
                          )}
                        </div>
                      )}
                      {type === 'other-school' && (
                        <Input
                          type={'text'}
                          id={`${objectName}.${index}.level`}
                          name={`${objectName}.${index}.level`}
                          label={t(`${translationPrefix}.classDetailsForm.level.label`)}
                          placeholder={t(`${translationPrefix}.classDetailsForm.level.placeholder`)}
                          defaultValue={classDetail.level}
                          error={
                            formik.touched?.[`${objectName}`]?.[index]?.['level'] &&
                            formik.errors[`${objectName}`]?.[index]?.['level']
                          }
                          onChange={(e) => {
                            formik.setFieldValue(`${objectName}.${index}.level`, e.target.value);
                          }}
                        />
                      )}
                    </div>
                  )}
                  <div className="col-span-1">
                    <Input
                      type={'select'}
                      options={Array.from({ length: 50 }, (_, i) => ({
                        value: i + 1,
                        label: i + 1,
                      }))}
                      id={`${objectName}.${index}.numberOfStudents`}
                      name={`${objectName}.${index}.numberOfStudents`}
                      label={t(`${translationPrefix}.classDetailsForm.numberOfStudents.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.numberOfStudents.placeholder`,
                      )}
                      defaultValue={classDetail.numberOfStudents}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['numberOfStudents'] &&
                        formik.errors[`${objectName}`]?.[index]?.['numberOfStudents']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(
                          `${objectName}.${index}.numberOfStudents`,
                          e.target.value,
                        );
                      }}
                    />
                  </div>
                  {type !== 'other-group' && (
                    <div className="col-span-1">
                      <Input
                        id={`${objectName}.${index}.sector`}
                        name={`${objectName}.${index}.sector`}
                        label={t(`${translationPrefix}.classDetailsForm.sector.label`)}
                        placeholder={t(`${translationPrefix}.classDetailsForm.sector.placeholder`)}
                        defaultValue={classDetail.sector}
                        error={
                          formik.touched?.[`${objectName}`]?.[index]?.['sector'] &&
                          formik.errors[`${objectName}`]?.[index]?.['sector']
                        }
                        onChange={(e) => {
                          formik.setFieldValue(`${objectName}.${index}.sector`, e.target.value);
                        }}
                      />
                    </div>
                  )}
                  <div className="col-span-1">
                    <Input
                      type={'radio'}
                      options={visitDays}
                      id={`${objectName}.${index}.visitDay`}
                      name={`${objectName}.${index}.visitDay`}
                      label={t(`${translationPrefix}.classDetailsForm.visitDay.label`)}
                      placeholder={t(`${translationPrefix}.classDetailsForm.visitDay.placeholder`)}
                      defaultValue={classDetail.visitDay}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['visitDay'] &&
                        formik.errors[`${objectName}`]?.[index]?.['visitDay']
                      }
                      disabled={(newBooking ? false : !editable) || slotLocked}
                      onChange={(value) => {
                        if ((!newBooking && !editable) || slotLocked) return;

                        formik.setFieldValue(`${objectName}.${index}.visitDay`, value);
                        formik.setFieldValue(`${objectName}.${index}.arrivalTime`, '');
                        formik.setFieldValue(`${objectName}.${index}.departureTime`, '');
                        formik.setFieldValue(`${objectName}.${index}.picnicTimeSlot`, '');
                        if (type === 'school') {
                          setIntroductionBooking(index, '');
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-1 flex w-full flex-col gap-4">
                    <Input
                      type={'select'}
                      options={modesOfTransport}
                      id={`${objectName}.${index}.modeOfTransport`}
                      name={`${objectName}.${index}.modeOfTransport`}
                      label={t(`${translationPrefix}.classDetailsForm.modeOfTransport.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.modeOfTransport.placeholder`,
                      )}
                      defaultValue={classDetail.modeOfTransport}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['modeOfTransport'] &&
                        formik.errors[`${objectName}`]?.[index]?.['modeOfTransport']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(
                          `${objectName}.${index}.modeOfTransport`,
                          e.target.value,
                        );
                      }}
                    />

                    {classDetail.modeOfTransport === 'other' && (
                      <Input
                        type={'text'}
                        id={`${objectName}.${index}.otherModeOfTransport`}
                        name={`${objectName}.${index}.otherModeOfTransport`}
                        label={t(
                          `${translationPrefix}.classDetailsForm.otherModeOfTransport.label`,
                        )}
                        placeholder={t(
                          `${translationPrefix}.classDetailsForm.otherModeOfTransport.placeholder`,
                        )}
                        defaultValue={classDetail.otherModeOfTransport}
                        error={
                          formik.touched?.[`${objectName}`]?.[index]?.['otherModeOfTransport'] &&
                          formik.errors[`${objectName}`]?.[index]?.['otherModeOfTransport']
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            `${objectName}.${index}.otherModeOfTransport`,
                            e.target.value,
                          );
                        }}
                      />
                    )}
                  </div>
                  <div className="col-span-1 md:col-span-2 grid grid-cols-1 gap-4 md:grid-cols-2 items-end">
                    <Input
                      type={'select'}
                      options={getArrivalTimeOptions(
                        classDetail?.visitDay,
                        classDetail.arrivalTime,
                        index,
                      )}
                      id={`${objectName}.${index}.arrivalTime`}
                      name={`${objectName}.${index}.arrivalTime`}
                      label={t(`${translationPrefix}.classDetailsForm.arrivalTime.label`)}
                      paragraphLabel={t(
                        `${translationPrefix}.classDetailsForm.arrivalTime.subLabel`,
                      )}
                      paragraphLabelClassName="text-sm text-gray-10 !font-normal"
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.arrivalTime.placeholder`,
                      )}
                      defaultValue={classDetail.arrivalTime}
                      disabled={slotLocked}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['arrivalTime'] &&
                        formik.errors[`${objectName}`]?.[index]?.['arrivalTime']
                      }
                      onChange={(e) => {
                        const arrivalTime = e.target.value;
                        formik.setFieldValue(`${objectName}.${index}.arrivalTime`, arrivalTime);
                        if (type === 'school') {
                          setIntroductionBooking(index, arrivalTime);
                        }

                        if (
                          classDetail.departureTime &&
                          !isDepartureAfterArrival(arrivalTime, classDetail.departureTime)
                        ) {
                          formik.setFieldValue(`${objectName}.${index}.departureTime`, '');
                        }
                      }}
                    />
                    <Input
                      type={'select'}
                      options={getDepartureTimeOptions(
                        classDetail?.visitDay,
                        classDetail.arrivalTime,
                      )}
                      id={`${objectName}.${index}.departureTime`}
                      name={`${objectName}.${index}.departureTime`}
                      label={t(`${translationPrefix}.classDetailsForm.departureTime.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.departureTime.placeholder`,
                      )}
                      defaultValue={classDetail.departureTime}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['departureTime'] &&
                        formik.errors[`${objectName}`]?.[index]?.['departureTime']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(
                          `${objectName}.${index}.departureTime`,
                          e.target.value,
                        );
                      }}
                    />
                  </div>

                  <div className="col-span-1">
                    <Input
                      type={'radio'}
                      options={[
                        {
                          value: 'false',
                          label: t(`${translationPrefix}.classDetailsForm.picnicOnSite.false`),
                        },
                        {
                          value: 'true',
                          label: t(`${translationPrefix}.classDetailsForm.picnicOnSite.true`),
                        },
                      ]}
                      id={`${objectName}.${index}.picnicOnSite`}
                      name={`${objectName}.${index}.picnicOnSite`}
                      paragraphLabel={t(`${translationPrefix}.classDetailsForm.picnicOnSite.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.picnicOnSite.placeholder`,
                      )}
                      defaultValue={classDetail.picnicOnSite}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['picnicOnSite'] &&
                        formik.errors[`${objectName}`]?.[index]?.['picnicOnSite']
                      }
                      onChange={(value) => {
                        formik.setFieldValue(`${objectName}.${index}.picnicOnSite`, value);
                        if (value !== 'true') {
                          formik.setFieldValue(`${objectName}.${index}.picnicTimeSlot`, '');
                        }
                      }}
                    />
                  </div>
                  {(classDetail.picnicOnSite === 'true' || classDetail.picnicOnSite === true) && (
                    <div className="col-span-1">
                      <Input
                        type={'select'}
                        options={picnicTimeSlots[classDetail.visitDay] || []}
                        id={`${objectName}.${index}.picnicTimeSlot`}
                        name={`${objectName}.${index}.picnicTimeSlot`}
                        label={t(`${translationPrefix}.classDetailsForm.picnicTimeSlot.label`)}
                        placeholder={t(
                          `${translationPrefix}.classDetailsForm.picnicTimeSlot.placeholder`,
                        )}
                        defaultValue={classDetail.picnicTimeSlot}
                        error={
                          formik.touched?.[`${objectName}`]?.[index]?.['picnicTimeSlot'] &&
                          formik.errors[`${objectName}`]?.[index]?.['picnicTimeSlot']
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            `${objectName}.${index}.picnicTimeSlot`,
                            e.target.value,
                          );
                        }}
                      />
                    </div>
                  )}
                </div>

                <>
                  <Text
                    className="mt-6 w-full text-base text-navy-10 text-center"
                    type={'medium'}
                    text={t(`${translationPrefix}.classDetailsForm.accessibilitiesText`)}
                  />
                  <Text
                    className="mb-6 w-full text-gray-10 text-center"
                    type={'xsmall'}
                    text={t(`${translationPrefix}.classDetailsForm.accessibilitiesTextDescription`)}
                  />

                  <Input
                    type={'radio'}
                    options={[
                      {
                        value: 'false',
                        label: t(`${translationPrefix}.classDetailsForm.handicap.false`),
                      },
                      {
                        value: 'true',
                        label: t(`${translationPrefix}.classDetailsForm.handicap.true`),
                      },
                    ]}
                    id={`${objectName}.${index}.handicap`}
                    name={`${objectName}.${index}.handicap`}
                    paragraphLabel={t(`${translationPrefix}.classDetailsForm.handicap.label`)}
                    paragraphLabelClassName="text-center"
                    radioClassName="justify-center"
                    placeholder={t(`${translationPrefix}.classDetailsForm.handicap.placeholder`)}
                    defaultValue={classDetail.handicap}
                    error={
                      formik.touched?.[`${objectName}`]?.[index]?.['handicap'] &&
                      formik.errors[`${objectName}`]?.[index]?.['handicap']
                    }
                    onChange={(value) => {
                      formik.setFieldValue(`${objectName}.${index}.handicap`, value);
                    }}
                  />

                  {classDetail.handicap === 'true' && (
                    <>
                      <Input
                        type={'textarea'}
                        id={`${objectName}.${index}.handicapDescription`}
                        name={`${objectName}.${index}.handicapDescription`}
                        inputClassName="block mt-2 md:max-w-md mx-auto"
                        placeholder={t(
                          `${translationPrefix}.classDetailsForm.handicapDescription.placeholder`,
                        )}
                        defaultValue={classDetail.handicapDescription}
                        error={
                          formik.touched?.[`${objectName}`]?.[index]?.['handicapDescription'] &&
                          formik.errors[`${objectName}`]?.[index]?.['handicapDescription']
                        }
                        errorClassName="text-center"
                        onChange={(e) => {
                          formik.setFieldValue(
                            `${objectName}.${index}.handicapDescription`,
                            e.target.value,
                          );
                        }}
                      />
                      <Text
                        className="mt-6 w-full text-xs text-gray-10 text-center"
                        type={'xsmall'}
                        text={t(`${translationPrefix}.classDetailsForm.accessibilitiesTextFooter`)}
                      />
                    </>
                  )}
                </>

                <span className="block my-10 bg-black h-[0.5px] w-full" />

                <>
                  <Text
                    className="mt-6 w-full text-base text-gray-10 text-center"
                    type={'medium'}
                    text={t(`${translationPrefix}.classDetailsForm.additionalInformationHeading`)}
                  />
                  <Text
                    className="mb-6 w-full text-gray-10 text-center"
                    type={'xsmall'}
                    text={t(
                      `${translationPrefix}.classDetailsForm.additionalInformationSubHeading`,
                    )}
                  />

                  <div className="mt-6">
                    <Text
                      className="w-full text-base text-gray-10 text-center"
                      type={'medium'}
                      text={t(`${translationPrefix}.classDetailsForm.responsibleDetailsHeading`)}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      <div className="col-span-1">
                        <Input
                          type={'text'}
                          id={`${objectName}.${index}.responsibleInformation.firstName`}
                          name={`${objectName}.${index}.responsibleInformation.firstName`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsFirstName.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsFirstName.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.firstName}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'firstName'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.firstName`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type={'text'}
                          id={`${objectName}.${index}.responsibleInformation.lastName`}
                          name={`${objectName}.${index}.responsibleInformation.lastName`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsLastName.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsLastName.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.lastName}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'lastName'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.lastName`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>

                      <div className="col-span-1">
                        <Input
                          type={'text'}
                          id={`${objectName}.${index}.responsibleInformation.function`}
                          name={`${objectName}.${index}.responsibleInformation.function`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsFunction.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsFunction.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.function}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'function'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.function`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type={'email'}
                          id={`${objectName}.${index}.responsibleInformation.email`}
                          name={`${objectName}.${index}.responsibleInformation.email`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsEmail.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsEmail.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.email}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'email'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.email`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type={'tel'}
                          id={`${objectName}.${index}.responsibleInformation.phone`}
                          name={`${objectName}.${index}.responsibleInformation.phone`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsPhone.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsPhone.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.phone}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'phone'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.phone`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>

                {visible &&
                  type === 'school' &&
                  !finalBookingTimePassed &&
                  classDetail?.numberOfStudents < NUMBER_OF_TOUR_STUDENTS.introduction.MIN && (
                    <p className="error-field text-red-20 text-xl font-semibold mt-6 text-center animate-bounce">
                      {t(`${translationPrefix}.classDetailsForm.introduction.numberOfStudents.min`)}
                    </p>
                  )}

                {visible &&
                  type === 'school' &&
                  !finalBookingTimePassed &&
                  classDetail?.numberOfStudents > NUMBER_OF_TOUR_STUDENTS.introduction.MAX && (
                    <p className="error-field text-red-20 text-xl font-semibold mt-6 text-center animate-bounce">
                      {t(`${translationPrefix}.classDetailsForm.introduction.numberOfStudents.max`)}
                    </p>
                  )}

                <div
                  className="mt-8 text-center"
                  dangerouslySetInnerHTML={{
                    __html: t(`${translationPrefix}.classDetailsForm.bottomText`),
                  }}
                />
              </div>
            )}
          </div>
        );
      })}

      <Popup
        open={openDeletePopup !== null}
        closePopup={() => setOpenDeletePopup(null)}
        confirmationPopup={{
          heading: t(`${translationPrefix}.classDetailsForm.deleteClass.heading`),
          subHeading: t(`${translationPrefix}.classDetailsForm.deleteClass.subHeading`),
          cancelButtonText: t(`${translationPrefix}.classDetailsForm.deleteClass.cancelButtonText`),
          cancelButtonCallback: () => {
            setOpenDeletePopup(null);
          },
          confirmButtonText: t(
            `${translationPrefix}.classDetailsForm.deleteClass.confirmButtonText`,
          ),
          confirmButtonCallback: () => {
            formik.setFieldValue(
              'classes',
              classDetailsFormikValues.filter((_, i) => i !== openDeletePopup),
            );
            setOpenDeletePopup(null);
          },
        }}
      />
    </div>
  );
};

export default ClassDetailsForm;
